import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { getMeta } from "./calendarTypes";
import { HOURS, HOUR_PX, eventGeometry, formatHM, isSameDay, gmtOffsetLabel, formatDayLong } from "./calendarHelpers";

/**
 * DayView — single-day desktop timeline (used when toggling "Jour").
 * Shows events laid out vertically against an hour axis.
 */
export default function DayView({ date, events, onEventClick, onSlotClick }) {
  const today = useMemo(() => new Date(), []);
  const isToday = isSameDay(date, today);
  const items = useMemo(
    () => events.filter((e) => isSameDay(new Date(e.starts_at), date))
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)),
    [events, date]
  );
  const now = new Date();
  const dayStartMin = HOURS[0] * 60;
  const dayEndMin = (HOURS[HOURS.length - 1] + 1) * 60;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const nowTop = ((nowMinutes - dayStartMin) / 60) * HOUR_PX;
  const nowVisible = isToday && nowMinutes >= dayStartMin && nowMinutes <= dayEndMin;
  const gridHeight = HOURS.length * HOUR_PX;

  return (
    <div
      className="rounded-2xl border border-border dark:border-border bg-card dark:bg-surface/40 overflow-hidden"
      data-testid="cal-day-view"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-border dark:border-border bg-secondary/50 dark:bg-surface/60">
        <h3 className="text-sm font-semibold capitalize">{formatDayLong(date)}</h3>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{gmtOffsetLabel()}</span>
      </div>
      <div className="overflow-y-auto max-h-[calc(100vh-340px)] min-h-[480px]">
        <div className="relative grid grid-cols-[64px_1fr]" style={{ height: gridHeight }}>
          {/* hours */}
          <div className="relative border-r border-border/70 dark:border-border">
            {HOURS.map((h, i) => (
              <div
                key={h}
                className="absolute left-0 right-0 px-2 text-[11px] tabular-nums text-muted-foreground dark:text-muted-foreground/80 -translate-y-1/2 text-right pr-3"
                style={{ top: i * HOUR_PX }}
              >
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>
          <div className="relative">
            {HOURS.map((h, i) => (
              <button
                key={h}
                onClick={() => {
                  const slot = new Date(date);
                  slot.setHours(h, 0, 0, 0);
                  onSlotClick?.(slot);
                }}
                className="absolute left-0 right-0 border-t border-border/70 dark:border-border hover:bg-accent/40 dark:hover:bg-secondary/60"
                style={{ top: i * HOUR_PX, height: HOUR_PX }}
                aria-label={`Créer à ${h}:00`}
              />
            ))}
            {nowVisible && (
              <div className="pointer-events-none absolute left-0 right-0 z-20" style={{ top: nowTop }}>
                <div className="relative h-[2px] bg-red-500/80">
                  <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-red-500" />
                </div>
              </div>
            )}
            {items.map((ev) => {
              const meta = getMeta(ev.type);
              const Icon = meta.Icon;
              const { top, height } = eventGeometry(ev.starts_at, ev.ends_at);
              return (
                <button
                  key={ev.id}
                  onClick={(e) => { e.stopPropagation(); onEventClick?.(ev); }}
                  className={cn(
                    "absolute left-3 right-3 z-10 rounded-lg border-l-[3px] px-3 py-2 text-left",
                    meta.bg, meta.border, meta.text
                  )}
                  style={{ top, height }}
                  data-testid={`day-event-${ev.id}`}
                >
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    <span className="text-sm font-semibold truncate">{ev.title}</span>
                    <Icon className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  </div>
                  <span className="text-[11px] opacity-80 tabular-nums">
                    {formatHM(ev.starts_at)} – {formatHM(ev.ends_at)}
                  </span>
                  {ev.location && (
                    <span className="block text-[11px] opacity-70 truncate">{ev.location}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
